/* eslint-env browser */
// OPC 点位曲线：导出 Excel（浏览器端用 xlsx 生成，不经后端出文件）。
// 数据是 history 接口 raw:true 的原始值；宽表 = 时间列 + 每个点位一列，从开始时间起每 10 秒一行（不超过当前时刻）。
// 历史值只在变化时记录，每格取该点位在这一行时刻生效的值（时间不晚于行时间的最后一个记录），10 秒内的短暂波动不进表。
// 后端在每条序列前补了「带入点」（start 之前最后一个值，时间记为 start），所以首行就有各点位在区间开始时生效的值；
// start 之前从没有记录的点位，第一个记录之前的行留空（不编值）。
import * as XLSX from "xlsx";
import {toServerTime} from "./opcCurveSettings";

const EXPORT_STEP_MS = 10 * 1000;
// Excel 单表最多 1048576 行，减去表头
const EXCEL_MAX_DATA_ROWS = 1048575;
// 每格都填值后浏览器端生成 xlsx 的内存近似与单元格数成正比，超过这个数基本会卡死或崩溃
const MAX_EXPORT_CELLS = 5000000;
const DAY_MS = 24 * 60 * 60 * 1000;
// 1970-01-01 的 Excel 序列号（1900 日期系统）
const EXCEL_EPOCH_SERIAL = 25569;
const TIME_FORMAT = 'yyyy-mm-dd hh:mm:ss';
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
}

function compactTime(date) {
    return date.getFullYear() + pad2(date.getMonth() + 1) + pad2(date.getDate())
        + pad2(date.getHours()) + pad2(date.getMinutes());
}

// epochMillis → Excel 日期序列号（按浏览器本地时区）。
// 不用 xlsx 自带的 Date 转换：它以本地时区的 1899-12-30 为基准，上海时区当年是 LMT +08:05:43，会差出 343 秒。
export function toExcelSerial(ms) {
    const offsetMs = new Date(ms).getTimezoneOffset() * 60 * 1000;
    return (ms - offsetMs) / DAY_MS + EXCEL_EPOCH_SERIAL;
}

export function opcCurveExcelFileName(deviceCode, range, middle) {
    return deviceCode + '_' + middle + '_' + compactTime(range[0]) + '-' + compactTime(range[1]) + '.xlsx';
}

/**
 * 导出的时间行：从 range[0] 起每 EXPORT_STEP_MS 一行，到 min(range[1], nowMs) 为止（不给未来时刻编值）。
 * 开始时间输入框精确到分钟，所以行时间落在整 10 秒上。
 *
 * @returns {{startMs: number, rowCount: number}} rowCount 不含表头，可能为 0（区间整个在未来）
 */
export function exportRows(range, nowMs) {
    const startMs = range[0].getTime();
    const lastMs = Math.min(range[1].getTime(), nowMs);
    const rowCount = lastMs < startMs ? 0 : Math.floor((lastMs - startMs) / EXPORT_STEP_MS) + 1;
    return {startMs, rowCount};
}

/** 行数超 Excel 上限或填满后单元格数超 MAX_EXPORT_CELLS 时为 true（数据点不计时间列） */
export function exportTooLarge(rowCount, columnCount) {
    return rowCount > EXCEL_MAX_DATA_ROWS || rowCount * columnCount > MAX_EXPORT_CELLS;
}

/**
 * @param columns  [{ nodeId, header }]，列顺序
 * @param series   history 接口返回 [{ nodeId, points: [[ms, value], ...] }]
 * @param timeHeader 时间列表头
 * @param rows     exportRows() 的结果
 * @returns {{workbook: object, rowCount: number, hasValue: boolean}} rowCount 不含表头；hasValue=false 表示所有格都空
 */
export function buildOpcCurveWorkbook(columns, series, timeHeader, rows) {
    const pointsByNode = {};
    series.forEach(s => {
        pointsByNode[s.nodeId] = s.points || [];
    });
    const columnPoints = columns.map(c => pointsByNode[c.nodeId] || []);
    const {startMs, rowCount} = rows;
    const aoa = new Array(rowCount + 1);
    aoa[0] = [timeHeader].concat(columns.map(c => c.header));
    for (let i = 0; i < rowCount; i++) {
        const row = new Array(columns.length + 1);
        row[0] = {t: 'n', v: toExcelSerial(startMs + i * EXPORT_STEP_MS), z: TIME_FORMAT};
        aoa[i + 1] = row;
    }
    // 每列的点按时间升序（后端保证）：每行取该时刻生效的值 = 时间 <= 行时间的最后一个点；第一个点之前留空
    let hasValue = false;
    columnPoints.forEach((points, j) => {
        let p = 0;
        for (let i = 0; i < rowCount; i++) {
            const t = startMs + i * EXPORT_STEP_MS;
            while (p < points.length && points[p][0] <= t) {
                p++;
            }
            if (p > 0) {
                aoa[i + 1][j + 1] = points[p - 1][1];
                hasValue = true;
            }
        }
    });
    const sheet = XLSX.utils.aoa_to_sheet(aoa);
    sheet['!cols'] = [{wch: 20}].concat(columns.map(c => ({wch: Math.min(60, Math.max(12, String(c.header).length * 2))})));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1');
    return {workbook, rowCount, hasValue};
}

function indexOfBytes(haystack, needle, limit) {
    const end = Math.min(haystack.length, limit) - needle.length;
    outer:
    for (let i = 0; i <= end; i++) {
        for (let j = 0; j < needle.length; j++) {
            if (haystack[i + j] !== needle[j]) {
                continue outer;
            }
        }
        return i;
    }
    return -1;
}

function asciiBytes(text) {
    const bytes = new Uint8Array(text.length);
    for (let i = 0; i < text.length; i++) {
        bytes[i] = text.charCodeAt(i);
    }
    return bytes;
}

// xlsx 0.18.5 社区版写 <sheetView> 时不带 <pane>（不支持冻结窗格），这里在生成的 zip 里给 sheet1.xml 补上「冻结首行」。
// 失败返回 null，调用方退回不冻结的 XLSX.writeFile。
function writeWithFrozenHeader(workbook) {
    const bytes = new Uint8Array(XLSX.write(workbook, {bookType: 'xlsx', type: 'array', compression: true}));
    const zip = XLSX.CFB.read(bytes, {type: 'array'});
    const entry = XLSX.CFB.find(zip, '/xl/worksheets/sheet1.xml');
    if (!entry || !entry.content) {
        return null;
    }
    const content = entry.content instanceof Uint8Array ? entry.content : Uint8Array.from(entry.content);
    const from = asciiBytes('<sheetView workbookViewId="0"/>');
    const at = indexOfBytes(content, from, 64 * 1024);
    if (at < 0) {
        return null;
    }
    const to = asciiBytes('<sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView>');
    const patched = new Uint8Array(content.length - from.length + to.length);
    patched.set(content.subarray(0, at), 0);
    patched.set(to, at);
    patched.set(content.subarray(at + from.length), at + to.length);
    XLSX.CFB.utils.cfb_add(zip, '/xl/worksheets/sheet1.xml', patched);
    return XLSX.CFB.write(zip, {fileType: 'zip', type: 'array', compression: true});
}

function download(bytes, fileName) {
    const url = URL.createObjectURL(new Blob([bytes], {type: XLSX_MIME}));
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function saveOpcCurveWorkbook(workbook, fileName) {
    let bytes = null;
    try {
        bytes = writeWithFrozenHeader(workbook);
    } catch (e) {
        console.warn('冻结首行失败，按普通工作表导出', e);
    }
    if (bytes) {
        download(bytes, fileName);
    } else {
        XLSX.writeFile(workbook, fileName);
    }
}

/**
 * 查原始值并导出。
 *
 * @param store      Vuex store（走 api/post，自行处理 400）
 * @param deviceCode 文件名用
 * @param range      [Date, Date] 查询时间范围
 * @param columns    [{ nodeId, header }] 勾选的点位
 * @param labels     { timeHeader, fileMiddle }
 * @returns Promise<{ rowCount }>；400（超 Excel 上限等）reject 一个带 userMessage 的 Error；
 *          区间内没有数据 reject 一个 empty=true 的 Error；
 *          填满后行数 / 单元格数过多（请求前就判定）reject 一个 tooLarge=true、带 rowCount / columnCount 的 Error；
 *          其它错误已由 api/error 统一提示
 */
export function exportOpcCurveExcel({store, deviceCode, range, columns, labels}) {
    const rows = exportRows(range, Date.now());
    if (!rows.rowCount) {
        const err = new Error('empty');
        err.empty = true;
        return Promise.reject(err);
    }
    if (exportTooLarge(rows.rowCount, columns.length)) {
        const err = new Error('too large');
        err.tooLarge = true;
        err.rowCount = rows.rowCount;
        err.columnCount = columns.length;
        return Promise.reject(err);
    }
    return store.dispatch('api/post', {
        url: process.env.VUE_APP_EM_OPCCLIENT_URL + '/emOpcPointCurve/history',
        data: {
            nodeIds: columns.map(c => c.nodeId),
            start: toServerTime(range[0]),
            end: toServerTime(range[1]),
            raw: true
        },
        commonErrorHandle: false,
        consoleLog: false
    }).catch(error => {
        const response = error && error.response;
        if (response && response.status === 400) {
            const err = new Error((response.data && response.data.message) || 'Bad Request');
            err.userMessage = err.message;
            throw err;
        }
        store.dispatch('api/error', {error});
        throw error;
    }).then(response => {
        const {workbook, rowCount, hasValue} = buildOpcCurveWorkbook(columns, response.data || [], labels.timeHeader, rows);
        if (!hasValue) {
            const err = new Error('empty');
            err.empty = true;
            throw err;
        }
        saveOpcCurveWorkbook(workbook, opcCurveExcelFileName(deviceCode, range, labels.fileMiddle));
        return {rowCount};
    });
}
